import { useEffect, useId, useState } from 'react';
import { Portal } from 'react-portal';
import { useTranslation } from 'react-i18next';

export default function CardExternalResource({
	title,
	description,
	url,
	credentials = {},
	termsAndConditions = '',
}) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const dialogId = useId();

	const open = () => setIsOpen(true);
	const close = () => setIsOpen(false);

	useEffect(() => {
		if (!isOpen) return;

		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		const onKeyDown = (e) => {
			if (e.key === 'Escape') close();
		};

		window.addEventListener('keydown', onKeyDown);
		return () => {
			document.body.style.overflow = prevOverflow;
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [isOpen]);

	const handleGoToUrl = () => {
		window.open(url, '_blank');
	};

	const formattedTerms = termsAndConditions
		.split('\n')
		.map((line) => line.trim())
		.filter(Boolean);

	return (
		<>
			{isOpen && (
				<Portal>
					<div
						className='fixed inset-0 z-[9999] flex items-center justify-center p-4'
						aria-labelledby={`${dialogId}-title`}
						role='dialog'
						aria-modal='true'
					>
						<div className='absolute inset-0 bg-black/60' onClick={close} />

						<div className='relative z-10 w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col'>
							<button
								type='button'
								onClick={close}
								aria-label={t('Close')}
								className='absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-500 text-white hover:bg-slate-600 z-10'
							>
								✕
							</button>

							<div className='p-5 md:p-6 overflow-y-auto'>
								<h2
									id={`${dialogId}-title`}
									className='text-lg md:text-xl font-semibold text-slate-900 mb-4 pr-10'
								>
									{title}
								</h2>

								<div className='mb-5'>
									<h3 className='text-sm font-semibold uppercase tracking-wide text-slate-500 mb-2'>
										{t('Terms and conditions')}
									</h3>
									<div className='text-[13px] text-slate-700 leading-6 bg-slate-50 rounded-lg p-4 border border-slate-200 max-h-[300px] overflow-y-auto'>
										{formattedTerms.map((line, index) => {
											const lowerLine = line.toLowerCase();
											const isHeading =
												index === 0 ||
												lowerLine.includes('student acceptance') ||
												lowerLine.includes('aceptacion del estudiante') ||
												lowerLine.includes('aceptacion de estudiante');

											const isBullet =
												line.startsWith('Modify,') ||
												line.startsWith('Attempt to') ||
												line.startsWith('Download,') ||
												line.startsWith('Use the platform') ||
												line.startsWith('Modificar,') ||
												line.startsWith('Intentar') ||
												line.startsWith('Descargar,') ||
												line.startsWith('Usar la plataforma');

											if (isHeading) {
												return (
													<p
														key={`${line}-${index}`}
														className='font-semibold text-slate-900 mt-1 mb-2'
													>
														{line}
													</p>
												);
											}

											if (isBullet) {
												return (
													<p
														key={`${line}-${index}`}
														className='pl-4 -indent-2 mb-1'
													>
														- {line}
													</p>
												);
											}

											return (
												<p key={`${line}-${index}`} className='mb-1'>
													{line}
												</p>
											);
										})}
									</div>
								</div>

								<div className='mb-6'>
									<h3 className='text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3'>
										{t('Credentials')}
									</h3>
									<div className='space-y-2 bg-slate-50 rounded-lg p-4 border border-slate-100'>
										<div className='flex items-center gap-3'>
											<span className='text-sm font-medium text-slate-600 w-20'>
												{t('Name')}:
											</span>
											<span className='text-sm text-slate-900 font-mono bg-white rounded px-3 py-1 border border-slate-200 select-all'>
												{credentials.name}
											</span>
										</div>
										<div className='flex items-center gap-3'>
											<span className='text-sm font-medium text-slate-600 w-20'>
												{t('Password')}:
											</span>
											<span className='text-sm text-slate-900 font-mono bg-white rounded px-3 py-1 border border-slate-200 select-all'>
												{credentials.password}
											</span>
										</div>
									</div>
								</div>

								<button
									type='button'
									onClick={handleGoToUrl}
									className='w-full rounded-xl bg-[var(--green-aquinas)] px-6 py-3 text-sm font-semibold text-white hover:opacity-90 transition-opacity'
								>
									{t('GO TO URL')}
								</button>
							</div>
						</div>
					</div>
				</Portal>
			)}

			<div className='bg-white border border-[rgba(181,187,190,0.53)] transition-all duration-300 hover:shadow-[0_0_10px_rgba(0,0,0,0.13)] min-h-[270px] min-w-0 flex flex-col'>
				<div className='h-[110px] md:h-[140px] w-full p-3 md:p-5'>
					<div className='h-full w-full bg-gradient-to-r from-[#114f73] to-[#2f7a9e] flex items-center justify-center'>
						<p className='text-white text-lg md:text-2xl font-semibold tracking-wide text-center px-2 leading-tight break-normal'>
							{title}
						</p>
					</div>
				</div>

				<div className='px-4 pb-5 md:px-5 md:pb-6 flex-1 flex flex-col'>
					<h3 className='text-[1.65rem] md:text-2xl leading-[1.15] font-semibold text-[#252525] mb-3 break-normal'>
						{title}
					</h3>
					<p className='text-sm md:text-base leading-6 text-[#252525] mb-6 line-clamp-3'>
						{description}
					</p>
					<button
						type='button'
						onClick={(e) => {
							e.stopPropagation();
							open();
						}}
						className='mt-auto inline-flex w-full items-center justify-center rounded-[9px] bg-[#1eb789] px-6 py-3 text-base font-bold tracking-wide text-white shadow-sm hover:bg-[#17a97d] transition-colors'
					>
						{t('OPEN')}
					</button>
				</div>
			</div>
		</>
	);
}
